/**
 * AWS Cognito Authentication Service
 * Handles user authentication using AWS Cognito User Pool
 * Uses AWS Amplify v6 Auth API
 */

import { signIn, signOut, getCurrentUser as amplifyGetCurrentUser, fetchAuthSession, fetchUserAttributes, type SignInOutput } from 'aws-amplify/auth';
import { User, UserRole } from '../types/user';

export interface CognitoUserAttributes {
  sub: string; // User ID
  email: string;
  'custom:username'?: string;
  'custom:role'?: string;
  email_verified?: string;
  name?: string;
}

/**
 * Convert Cognito user attributes to our User type
 */
const cognitoUserToUser = (attributes: Partial<Record<string, string>>, username: string): User => {
  return {
    id: attributes.sub || '',
    username: attributes['custom:username'] || username,
    email: attributes.email || '',
    role: (attributes['custom:role'] as UserRole) || 'user',
    createdAt: new Date().toISOString(), // Cognito doesn't provide this, use current time
    updatedAt: new Date().toISOString(),
    isActive: true,
  };
};

export const cognitoService = {
  /**
   * Sign in user with username and password
   */
  async login(username: string, password: string): Promise<{ user: User; token: string } | null> {
    try {
      const output: SignInOutput = await signIn({
        username,
        password,
      });

      // Handle MFA or other challenges if needed
      if (output.isSignedIn) {
        // User is signed in, get user details
        const session = await fetchAuthSession();
        
        if (!session.tokens) {
          throw new Error('Failed to get authentication tokens');
        }

        // Get user attributes
        const attributes = await fetchUserAttributes();
        const currentUser = await amplifyGetCurrentUser();

        const user = cognitoUserToUser(attributes, currentUser.username);
        const token = session.tokens.idToken?.toString() || '';

        return { user, token };
      } else if (output.nextStep) {
        // Handle additional steps (MFA, new password, etc.)
        if (output.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_SMS_CODE' || 
            output.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
          throw new Error('MFA is required. Please implement MFA confirmation.');
        } else if (output.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
          throw new Error('New password required. Please change your password.');
        }
      }

      return null;
    } catch (error: any) {
      console.error('Cognito login error:', error);
      // Convert Amplify errors to user-friendly messages
      if (error.name === 'NotAuthorizedException') {
        throw new Error('Invalid username or password');
      } else if (error.name === 'UserNotFoundException') {
        throw new Error('User not found');
      } else if (error.name === 'UserNotConfirmedException') {
        throw new Error('User account is not confirmed. Please check your email.');
      }
      throw error;
    }
  },

  /**
   * Sign out current user
   */
  async logout(): Promise<void> {
    try {
      await signOut();
    } catch (error) {
      console.error('Cognito logout error:', error);
      throw error;
    }
  },

  /**
   * Get current authenticated user
   */
  async getCurrentUser(): Promise<User | null> {
    try {
      const currentUser = await amplifyGetCurrentUser();
      const session = await fetchAuthSession();
      
      if (!session.tokens) {
        return null;
      }

      // Get user attributes
      const attributes = await fetchUserAttributes();

      return cognitoUserToUser(attributes, currentUser.username);
    } catch (error) {
      // User not authenticated
      return null;
    }
  },

  /**
   * Check if user is authenticated
   */
  async isAuthenticated(): Promise<boolean> {
    try {
      const user = await this.getCurrentUser();
      return user !== null;
    } catch {
      return false;
    }
  },

  /**
   * Get current session tokens
   */
  async getSession() {
    try {
      return await fetchAuthSession();
    } catch {
      return null;
    }
  },
};

